// import React from 'react';
// import { motion } from 'framer-motion';

// const CreditCardDisplay = ({ cardData }) => {
//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 20 }}
//       animate={{ opacity: 1, y: 0 }}
//       transition={{ duration: 0.5 }}
//       className="relative w-full max-w-md mx-auto group"
//     >
//       <div className="bg-gradient-to-br from-green-700 via-green-600 to-green-800 rounded-3xl p-6 text-white shadow-2xl hover:shadow-3xl transition-all duration-300 group-hover:scale-105 dark:from-green-800 dark:via-green-700 dark:to-green-900">
//         <div className="flex justify-between items-start mb-8">
//           <div className="text-sm opacity-90 font-medium">Credit Card</div>
//           <div className="w-12 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg opacity-90 shadow-inner"></div>
//         </div>
        
//         <div className="mb-6">
//           <div className="text-lg font-mono tracking-wider font-semibold">
//             •••• •••• •••• {cardData.lastFourDigits}
//           </div>
//         </div>
        
//         <div className="flex justify-between items-end">
//           <div>
//             <div className="text-xs opacity-75 mb-1 font-medium">CARD HOLDER</div>
//             <div className="font-semibold text-sm tracking-wide">{cardData.holderName}</div>
//           </div>
//           <div className="text-right">
//             <div className="text-xs opacity-75 mb-1 font-medium">EXPIRES</div>
//             <div className="font-mono text-sm font-semibold">{cardData.expiryDate}</div>
//           </div>
//         </div>
//       </div>
//       <div className="absolute inset-0 bg-gradient-to-br from-green-600/20 to-green-800/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
//     </motion.div>
//   );
// };

// export default CreditCardDisplay;



import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring, animate } from 'framer-motion';

const CARD_THEMES = {
  '478972': { name: 'Classic', color: '#2B337D', highlight: '#3d4db5' },
  '493465': { name: 'Gold',    color: '#DEBE5C', highlight: '#f5d97e' },
  '470556': { name: 'Platinum',color: '#212121', highlight: '#4a4a4a' }
};

// Floating particle positions (deterministic, no random)

const PARTICLES = [
  { x: '10%', y: '20%', size: 6,  delay: 0.0  },
  { x: '80%', y: '10%', size: 4,  delay: 0.4  },
  { x: '65%', y: '75%', size: 8,  delay: 0.8  },
  { x: '25%', y: '80%', size: 5,  delay: 1.2  },
  { x: '90%', y: '50%', size: 4,  delay: 0.2  },
  { x: '45%', y: '15%', size: 6,  delay: 1.6  },
  { x: '55%', y: '55%', size: 3,  delay: 0.6  },
  { x: '18%', y: '45%', size: 5,  delay: 1.0  },
  { x: '72%', y: '30%', size: 4,  delay: 1.4  },
  { x: '35%', y: '60%', size: 7,  delay: 0.3  },
  { x: '85%', y: '70%', size: 3,  delay: 1.9  },
  { x: '50%', y: '88%', size: 5,  delay: 0.7  },
  { x: '8%',  y: '65%', size: 4,  delay: 1.1  },
  { x: '60%', y: '42%', size: 6,  delay: 2.1  },
  { x: '30%', y: '25%', size: 3,  delay: 0.5  },
  { x: '92%', y: '85%', size: 5,  delay: 1.7  },
  { x: '42%', y: '72%', size: 4,  delay: 0.9  },
  { x: '75%', y: '18%', size: 3,  delay: 2.3  },
  { x: '15%', y: '90%', size: 6,  delay: 1.3  },
  { x: '68%', y: '60%', size: 4,  delay: 0.1  },
];

const CreditCardDisplay = ({ cardData }) => {
  const theme = CARD_THEMES[cardData.cardNoFirstSixDigits] || CARD_THEMES['478972'];
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  // Mouse-tracking 3D tilt
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [12, -12]), { stiffness: 200, damping: 20 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-12, 12]), { stiffness: 200, damping: 20 });
  const glareX = useTransform(mouseX, [-0.5, 0.5], ['0%', '100%']);
  const glareY = useTransform(mouseY, [-0.5, 0.5], ['0%', '100%']);

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top)  / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    animate(mouseX, 0, { type: 'spring', stiffness: 200, damping: 20 });
    animate(mouseY, 0, { type: 'spring', stiffness: 200, damping: 20 });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.92 }}
      animate={{ opacity: 1, y: 0,  scale: 1    }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="relative w-full max-w-md mx-auto"
      style={{ perspective: 1000 }}
    >
      {/* Pulsing outer glow */}
      <motion.div
        className="absolute inset-0 rounded-3xl -z-10 blur-2xl"
        style={{ backgroundColor: theme.color }}
        animate={{ opacity: [0.2, 0.45, 0.2], scale: [1, 1.05, 1] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* 3D tilt card */}
      <motion.div
        ref={cardRef}
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
        className="relative rounded-3xl cursor-pointer"
      >
        {/* Card body */}
        <div
          style={{ backgroundColor: theme.color }}
          className="relative rounded-3xl p-8 text-white shadow-2xl overflow-hidden"
        >
          {/* Shimmer sweep */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.18) 50%, transparent 60%)`,
              backgroundSize: '200% 100%',
            }}
            animate={{ backgroundPositionX: ['-100%', '200%'] }}
            transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.5, ease: 'easeInOut' }}
          />

          {/* Mouse-follow glare */}
          {isHovered && (
            <motion.div
              className="absolute inset-0 rounded-3xl pointer-events-none"
              style={{
                background: `radial-gradient(circle at ${glareX} ${glareY}, rgba(255,255,255,0.12) 0%, transparent 60%)`,
              }}
            />
          )}

          {/* Floating particles */}
          {PARTICLES.map((p, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white pointer-events-none"
              style={{ left: p.x, top: p.y, width: p.size, height: p.size, opacity: 0.15 }}
              animate={{ y: [0, -10, 0], opacity: [0.1, 0.3, 0.1] }}
              transition={{ duration: 2.8, repeat: Infinity, delay: p.delay, ease: 'easeInOut' }}
            />
          ))}

          {/* Decorative BG circles */}
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full opacity-10"
            style={{ backgroundColor: theme.highlight }} />
          <div className="absolute -left-8 -bottom-8 w-32 h-32 rounded-full opacity-10"
            style={{ backgroundColor: theme.highlight }} />

          {/* Header row */}
          <div className="relative flex justify-between items-start mb-10">
            <motion.div
              className="text-sm font-bold uppercase tracking-widest opacity-90"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              {theme.name}
            </motion.div>

            {/* Animated chip */}
            <motion.div
              className="w-12 h-9 rounded-lg shadow-inner"
              style={{ background: 'linear-gradient(135deg, #f5e27a, #c9a227, #f5e27a)' }}
              animate={{ boxShadow: ['0 0 0px #f5e27a', '0 0 12px #f5e27a88', '0 0 0px #f5e27a'] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              {/* Chip lines */}
              <div className="w-full h-full rounded-lg overflow-hidden opacity-40 flex flex-col justify-center items-center gap-1 p-1">
                <div className="w-full h-px bg-yellow-900/60" />
                <div className="w-full h-px bg-yellow-900/60" />
                <div className="w-full h-px bg-yellow-900/60" />
              </div>
            </motion.div>
          </div>

          {/* Card number */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.45 }}
          >
            <div className="text-xl font-mono tracking-[0.25em] font-semibold">
              •••• &nbsp;•••• &nbsp;•••• &nbsp;{cardData.lastFourDigits}
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            className="flex justify-between items-end"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
          >
            <div className="flex flex-col gap-1">
              <div className="text-[10px] opacity-60 font-bold tracking-widest uppercase">CardHolder</div>
              <div className="font-semibold text-sm tracking-wide">{cardData.holderName}</div>
            </div>
            <div className="flex flex-col gap-1 text-right">
              <div className="text-[10px] opacity-60 font-bold tracking-widest uppercase">Expires</div>
              <div className="font-mono text-sm font-semibold">******</div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CreditCardDisplay;
